// HelpChat.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Footer from "../../Component/Footer/Footer";

import { call } from "../../assets/img";

// Firebase
import {
  ref,
  get,
  query,
  orderByChild,
  limitToLast,
  onChildAdded,
  update,
  orderByKey,
} from "firebase/database";
import { db } from "../../Services/firebase";

import { post } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";
import CustomScroll from "../../Component/CustomScroll";
import useStore from "../redux/store/useStore";

const HelpChat = () => {
  const location = useLocation();
  const chatId = location?.state?.chatId || "";
  const mobile = location?.state?.mobile || "";
  const clientName = location?.state?.client_name || "";

  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [loader, setLoader] = useState(false);

  const scrollRef = useRef(null);
  const footerRef = useRef(null);
  const { user } = useStore();
  const currentUserId = user?.id;
  const [footerSpace, setFooterSpace] = useState(100);

  // 👉 Scroll to bottom
  const scrollToBottom = () => {
    try {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    } catch { }
  };

  const markMessagesAsSeen = async () => {
    if (!chatId) return;

    const messagesRef = ref(db, `chat/${chatId}`);
    const q = query(messagesRef, orderByKey());
    const snapshot = await get(q);

    if (!snapshot.exists()) return;

    const updates = {};

    snapshot.forEach((child) => {
      const message = child.val();

      // Only update:
      // 1. Not my message
      // 2. Not already seen
      if (
        message?.sender_id !== currentUserId &&
        message?.seen === false
      ) {
        updates[`${child.key}/seen`] = true;
      }
    });

    if (Object.keys(updates).length > 0) {
      await update(messagesRef, updates);
    }
  };

  // ---------------------------------------------------------------------
  // LOAD INITIAL MESSAGES
  // ---------------------------------------------------------------------
  const loadInitialMessages = useCallback(async () => {
    if (!chatId) return;

    const q = query(ref(db, `chat/${chatId}`), limitToLast(50));
    const snap = await get(q);

    let list = [];

    snap.forEach((child) => {
      const v = child.val();
      list.push({
        id: child.key,
        sender_id: v.is_admin_sender == 1 ? "admin" : "user",
        text: v.text || v.message || "",
        created_at: v.created_at || v.time || 0,
        admin_name: v.admin_name || '',
      });
    });

    list.sort((a, b) => a.created_at - b.created_at);
    setMessages(list);
//     console.log("list chat", list);
    markMessagesAsSeen();
    // setTimeout(scrollToBottom, 200);
  }, [chatId]);

  // ---------------------------------------------------------------------
  // REALTIME LISTENER (NEW MESSAGES)
  // ---------------------------------------------------------------------
  const listenRealtime = useCallback(() => {
    if (!chatId) return;

    const q = query(ref(db, `chat/${chatId}`), orderByChild("created_at"));

    const unsubscribe = onChildAdded(q, (snap) => {
      const v = snap.val();
      if (!v) return;
//       console.log("vvvvv", v);

      const newMsg = {
        id: snap.key,
        sender_id: v.is_admin_sender == 1 ? "admin" : "user",
        text: v.text || v.message || "",
        created_at: v.created_at || v.time || '',
        admin_name: v.admin_name || '',
      };

      // prevent duplicates
      setMessages((prev) => {
        const exists = prev.some((m) => m.id === newMsg.id);
        if (exists) return prev;
        return [...prev, newMsg];
      });

      // setTimeout(scrollToBottom, 100);
    });

    return unsubscribe;
  }, [chatId]);

  // ---------------------------------------------------------------------
  // INIT
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!chatId) return;

    setMessages([]);

    loadInitialMessages();

    const unsubscribe = listenRealtime();
    return () => unsubscribe && unsubscribe();
  }, [chatId, loadInitialMessages, listenRealtime]);

  // ---------------------------------------------------------------------
  // SEND MESSAGE
  // ---------------------------------------------------------------------
  const sendMessage = async () => {
    if (!msg.trim()) return;

    setLoader(true);

    const body = {
      client_id: chatId,
      message: msg,
    };

    try {
      await post(URLS.CHATSEND, body);
      setMsg("");

      setTimeout(scrollToBottom, 100);
    } catch (e) {
//       console.log("send error", e);
    } finally {
      setLoader(false);
    }
  };

  // ---------------------------------------------------------------------
  // MESSAGE CARD
  // ---------------------------------------------------------------------
  const MessageCard = ({ data }) => {
    const isRight = data.sender_id === "admin";
//     console.log("data fireeee", data);

    return (
      <div className={`col-md-12 mt-2 ${isRight ? "ac-je" : "ac-js"}`}>
        <div className="alignCCC" style={{ alignItems: isRight ? "flex-end" : "flex-start", textAlign: isRight ? "right" : "left" }}>
          <p className="f3 mb-1 primary1">{isRight ? data?.admin_name : "User"}</p>

          <p style={{
            resize: 'none',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            maxWidth: '90%',
          }} className={`${isRight ? "User_box" : "Admin_box"} mb-1`}>
            {data.text}
          </p>

          <p className="f1 primary1">
            {/* {moment(data.created_at).format("DD MMM hh:mm A")} */}
            {data.created_at}
          </p>
        </div>
      </div>
    );
  };


  const [browsHeader, setBrowsHeader] = useState(true);

  const maxHeight = useRef(window.visualViewport?.height || window.innerHeight);
  const isFirstRun = useRef(true);

  useEffect(() => {
    const handleResize = () => {
      const visibleHeight = window.visualViewport?.height || window.innerHeight;

      // 🔹 Skip logic on first run
      if (isFirstRun.current) {
        isFirstRun.current = false;
        setBrowsHeader(true);
        return;
      }

      // 🔻 Viewport reduced (keyboard / browser UI)
      if (visibleHeight < maxHeight.current) {
        setBrowsHeader(true);
      }
      // 🔺 Viewport restored
      else {
        maxHeight.current = visibleHeight;
        setBrowsHeader(false);
      }
    };

    window.visualViewport?.addEventListener("resize", handleResize);

    return () => {
      window.visualViewport?.removeEventListener("resize", handleResize);
    };
  }, [browsHeader]);

  useEffect(() => {
    const updateFooterSpace = () => {
      const footerHeight = footerRef.current?.offsetHeight || 0;
      setFooterSpace(footerHeight + 20);
    };

    updateFooterSpace();

    window.addEventListener("resize", updateFooterSpace);
    window.visualViewport?.addEventListener("resize", updateFooterSpace);

    return () => {
      window.removeEventListener("resize", updateFooterSpace);
      window.visualViewport?.removeEventListener("resize", updateFooterSpace);
    };
  }, []);

  useEffect(() => {
    const rafId = window.requestAnimationFrame(scrollToBottom);
    return () => window.cancelAnimationFrame(rafId);
  }, [messages.length, footerSpace]);

  return (
    <div className="dashRightView">
      <CustomScroll >
        <div className="container_overalls">
          <form className="col-md-12">



            {/* CHAT LIST */}
            <div className="  col-md-5 ">

              <div
                ref={scrollRef}
                className="user-scroll"
                style={{
                  maxHeight: 420,
                  overflowY: "auto",
                  width: "100%",
                  paddingBottom: footerSpace,
                }}
              >
                {/* USER ID */}
                <div className="col-md-5 ">
                  <label className="form-label f3">User Name</label>
                  <input className="input-box f3" style={{ color: '#565353' }} value={clientName} disabled />
                </div>

                {/* MOBILE */}
                <div className="form-group col-md-5 text-start my-3">
                  <label className="form-label f3">Mobile No</label>
                  <div className="login-input1 mt-1" style={{ display: "flex" }}>
                    <input className="f3" value={mobile} disabled style={{ flex: 1 }} />
                    <img src={call} alt="call" style={{ width: 20, marginRight: 12 }} />
                  </div>
                </div>
                {messages.map((m) => (
                  <MessageCard key={m.id} data={m} />
                ))}
              </div>
            </div>
            <div ref={footerRef} className="chat-footer-fix">
              {/* INPUT */}
              <div className="form-group col-md-5 text-start my-3">
                <div className="login-input col-md-5 mt-3">
                  <input
                    className="input-box f3"
                    value={msg}
                    onChange={(e) => setMsg(e.target.value)}
                    placeholder="Type..."
                  />
                </div>
              </div>

              {/* SEND */}
              <div className="col-md-5 my-3">
                <button
                  style={{ marginBottom: browsHeader ? 60 : null }}
                  type="button"
                  className="add-button w-100 f3 white"
                  disabled={!msg.trim() || loader}
                  onClick={sendMessage}
                >
                  {loader ? "Sending..." : "Reply"}
                </button>
              </div>
            </div>
          </form>

          <Footer />
        </div>
      </CustomScroll>
    </div>
  );
};

export default HelpChat;
